<template>
  <v-app>
    <div
      class="doctor-filter-panel"
      :class="{
        'doctor-filter-panel_desktop': !isMobile,
        'doctor-filter-panel_short': !urlDisease && !isMobile,
      }"
      data-qa="doctor_filter_panel"
    >
      <div
        class="bg-gray-0--text"
        :class="isMobile ? 'text-subtitle-1 px-4 pt-4 pb-3' : 'text-h6 py-5 pl-4 pr-16 mr-4'"
      >
        {{ t('Выбрать врача', options) }}
      </div>
      <v-list class="doctor-filter-panel__list">
        <v-list-item
          class="doctor-filter-panel__list-item"
          data-qa="doctor_filter_panel_specialities"
          @click="openModalSpecialities"
        >
          <div class="d-flex align-center w-100">
            <v-icon class="doctor-filter-panel__item-icon success">
              ui-icon-stetoscope-40
            </v-icon>
            <div class="text-body-1 text--text">
              {{ t('По специальности', options) }}
            </div>
            <v-icon class="ml-auto pl-4">
              ui-icon-arrow-right
            </v-icon>
          </div>
        </v-list-item>
        <v-list-item
          v-if="urlDisease"
          class="doctor-filter-panel__list-item"
          data-qa="doctor_filter_panel_disease"
          :href="urlDisease"
          @click="handleClickUrlDisease"
        >
          <div class="d-flex align-center w-100">
            <v-icon class="doctor-filter-panel__item-icon secondary">
              ui-icon-like-40
            </v-icon>
            <div class="text-body-1 text--text">
              {{ t('По заболеванию', options) }}
            </div>
            <v-icon class="ml-auto pl-4">
              ui-icon-arrow-right
            </v-icon>
          </div>
        </v-list-item>
      </v-list>

      <CoreSelectionDrawer
        :value="isDialogOpened"
        has-search
        has-footer
        :title="t('Специальность', options)"
        :items="specialities"
        :loading="loading"
        :error="error"
        @click-item="handleClickSpeciality"
        @input="closeDialog"
      />
    </div>
  </v-app>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import I18NextVue from 'i18next'
import YaGoals from 'www/modules/YandexGoals'
import UserStorage from 'www/modules/UserStorage'
import useDialogManager from 'www/vue_components/common/mixins/DialogManager/useDialogManager'
import CoreSelectionDrawer from 'src/core/CoreSelectionDrawer/CoreSelectionDrawer.vue'
import apiTownSpecialities from 'www/vue_components/common/TheFilters/doctors/api/apiTownSpecialities'
import type { ApiTownSpecialitiesResponseGet } from 'www/vue_components/common/TheFilters/doctors/api/apiTownSpecialities.types'

const props = defineProps<{
  townId: number
  townTranslit: string
  urlDisease?: string
  isDoctorListOnline?: boolean
  isDoctorListHome?: boolean
}>()

const { t, language } = I18NextVue
const options = computed(() => ({ ns: language }))

const {
  townId,
  townTranslit,
  isDoctorListOnline,
  isDoctorListHome,
} = props

const isMobile = window.MOBILE_VERSION
const loading = ref(false)
const error = ref(false)
const instanceYaGoals = new YaGoals()

const { isDialogOpened, openDialog, closeDialog } = useDialogManager('DoctorFilterPanel')

const specialities = ref<ApiTownSpecialitiesResponseGet>([])

const unwatch = watch(() => isDialogOpened.value, async () => {
  if (isDialogOpened.value && !specialities.value.length) {
    await loadSpecialities()

    unwatch()
  }
}, { immediate: true })

async function loadSpecialities() {
  error.value = false
  loading.value = true

  try {
    const { data } = await apiTownSpecialities.get({
      town_id: isDoctorListOnline ? undefined : townId,
      is_house_filter: isDoctorListHome || undefined,
      is_town_online_filter: isDoctorListOnline || undefined,
    })

    specialities.value = data
  } catch {
    error.value = true
  } finally {
    loading.value = false
  }
}

async function openModalSpecialities() {
  openDialog()

  if (!specialities.value.length) {
    await loadSpecialities()
  }

  instanceYaGoals.send({ name: 'townDoctorListFilter__openModalSpecialities' })
}

function handleClickSpeciality(speciality: ApiTownSpecialitiesResponseGet[number]) {
  closeDialog()

  instanceYaGoals.send({ name: 'townDoctorListFilter__clickItemSpeciality' })

  UserStorage.set('chosen-speciality', speciality.name)

  const { currentLanguage, defaultLanguageIso, availableLanguages } = window.LANGUAGE_DATA
  const iso = availableLanguages.length && currentLanguage.iso !== defaultLanguageIso ? `/${currentLanguage.iso}` : ''

  if (isDoctorListOnline) {
    window.location.assign(`${iso}/online/${speciality.translit}/`)
  } else if (isDoctorListHome) {
    window.location.assign(`${iso}/${townTranslit}/${speciality.translit}/na-dom/`)
  } else {
    window.location.assign(`${iso}/${townTranslit}/${speciality.translit}/`)
  }
}

function handleClickUrlDisease() {
  instanceYaGoals.send({ name: 'townDoctorListFilter__clickUrlDisease' })
}
</script>

<style scoped lang="scss">
@import '~www/themes/doctors/common/variables';

.doctor-filter-panel {
  display: block;
  overflow: hidden;
  border-radius: $border-radius-lg;
  background-color: $ui-kit-primary;
  padding: 2px;

  &__list {
    overflow: hidden;
    border-radius: 10px;
  }

  &__list-item {
    padding: 8px 16px;
    color: $ui-kit-primary !important;
  }

  &__item-icon {
    padding: 8px;
    margin-right: 16px;
    border-radius: $border-radius-lg;
    color: $ui-kit-bg-gray-0;
  }

  &_short {
    max-width: 67%;
  }

  &_desktop {
    display: flex;
  }

  &_desktop &__list {
    display: flex;
    flex-grow: 1;
  }
}
</style>
